/**
 * A draggable "Suggested times" block for the rich email editor.
 *
 * "Insert times" drops this single custom node into the editor. The user can
 * drag it around, select/delete it as one unit, switch its style inline, and
 * click into the heading AND the footer line to edit them. Slots stay
 * attribute-driven (they're generated from the picked times).
 *
 * The node's inline content IS the heading text (content: "inline*"). The
 * footer lead-in and "more times" label live as attributes so we don't have
 * to invent a two-region document schema. On send, `renderToReactEmail` reads
 * the live heading from content and the live footer from attrs.
 *
 * Round-tripping (copy/paste, reload-from-HTML) goes through parseHTML/renderHTML
 * which carry the attrs on a `div[data-suggested-times]` wrapper plus the heading
 * text as the element's content. The DOM renderHTML emits is only the editor-side
 * fallback; the real email output comes from renderToReactEmail.
 */
import { useEffect, useRef } from "react";
import { EmailNode } from "@react-email/editor/core";
import { mergeAttributes, type JSONContent } from "@tiptap/core";
import {
    ReactNodeViewRenderer,
    NodeViewWrapper,
    NodeViewContent,
    type NodeViewProps,
} from "@tiptap/react";
import {
    buildSuggestedTimesHtml,
    DEFAULT_SUGGESTED_TIMES_FOOTER,
    DEFAULT_SUGGESTED_TIMES_MORE,
    friendlyTimeZone,
    type SuggestedTimesStyle,
} from "../../lib/booking";

/** The attributes a suggested-times node carries (everything BUT the heading,
 *  which lives as the node's editable inline content). Mirrors the args of
 *  buildSuggestedTimesHtml so the node maps straight onto the email markup. */
export interface SuggestedTimesAttrs {
    token: string;
    /** Slot start ISO strings, in display order. */
    slots: string[];
    slug?: string;
    timeZone: string;
    bookingUrlBase: string;
    /** The default heading text seeded into the node's content on insert. */
    heading?: string;
    /** Lead-in before the "more times" link. */
    footer?: string;
    /** Label for the booking-page link. */
    moreLabel?: string;
    style: SuggestedTimesStyle;
}

/** Build the TipTap insert payload for a suggested-times node: the attrs minus
 *  the heading, plus the heading seeded as the node's editable inline text. If
 *  no explicit heading is given, the default folds in the friendly timezone
 *  (e.g. "Suggested times (all Eastern)"), matching buildSuggestedTimesHtml's
 *  default — so the inserted node already shows editable heading text. */
export function suggestedTimesInsertContent(attrs: SuggestedTimesAttrs): JSONContent {
    const { heading, ...rest } = attrs;
    const headingText =
        heading ?? `Suggested times (all ${friendlyTimeZone(attrs.timeZone)})`;
    return {
        type: "suggestedTimes",
        attrs: rest,
        content: [{ type: "text", text: headingText }],
    };
}

/** Coerce the loosely-typed node.attrs into the builder's input shape (sans
 *  heading — that comes from the node's content). */
function attrsFromNode(attrs: Record<string, unknown> | undefined): Omit<SuggestedTimesAttrs, "heading"> {
    const slotsRaw = attrs?.slots;
    const slots = Array.isArray(slotsRaw)
        ? (slotsRaw as string[])
        : typeof slotsRaw === "string" && slotsRaw
          ? safeParseSlots(slotsRaw)
          : [];
    const style = (attrs?.style as SuggestedTimesStyle) ?? "text";
    return {
        token: String(attrs?.token ?? ""),
        slots,
        slug: attrs?.slug ? String(attrs.slug) : undefined,
        timeZone: String(attrs?.timeZone ?? "America/New_York"),
        bookingUrlBase: String(attrs?.bookingUrlBase ?? ""),
        footer: typeof attrs?.footer === "string" ? attrs.footer : DEFAULT_SUGGESTED_TIMES_FOOTER,
        moreLabel: typeof attrs?.moreLabel === "string" ? attrs.moreLabel : DEFAULT_SUGGESTED_TIMES_MORE,
        style,
    };
}

function safeParseSlots(value: string): string[] {
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
        return [];
    }
}

/** Pull the plain heading text out of a node's inline content. The serializer
 *  hands us a JSONContent whose `content` is the heading's inline text nodes;
 *  flatten them to a string so the email heading reflects live edits. */
function headingTextFromContent(content: JSONContent["content"]): string {
    if (!Array.isArray(content)) return "";
    return content
        .map(child => (typeof child.text === "string" ? child.text : ""))
        .join("")
        .trim();
}

/** Build the email-safe HTML for a node, using its CURRENT heading text. When
 *  the heading is empty (user cleared it) we let buildSuggestedTimesHtml fall
 *  back to its own default by omitting `heading`. */
function emailHtmlForNode(node: JSONContent): string {
    const attrs = attrsFromNode(node.attrs);
    const heading = headingTextFromContent(node.content);
    return buildSuggestedTimesHtml({ ...attrs, heading: heading || undefined });
}

/** The email-side renderer. The serializer passes `{ node }` (a JSONContent
 *  whose `content` holds the live heading text and `attrs` the rest); we emit
 *  the same email-safe HTML the standalone builder produces. */
function SuggestedTimesEmail({ node }: { node: JSONContent }) {
    return <div dangerouslySetInnerHTML={{ __html: emailHtmlForNode(node) }} />;
}

const STYLE_OPTIONS: ReadonlyArray<{ value: SuggestedTimesStyle; label: string }> = [
    { value: "widget", label: "Widget" },
    { value: "buttons", label: "Buttons" },
    { value: "links", label: "Links" },
    { value: "text", label: "Text" },
];

/** Uncontrolled contentEditable bit that writes back to a node attr on blur.
 *  Kept out of the TipTap document (the heading already occupies `content`)
 *  so Enter / backspace here can't split the block. */
function EditableAttr({
    value,
    onChange,
    className,
}: {
    value: string;
    onChange: (next: string) => void;
    className?: string;
}) {
    const ref = useRef<HTMLSpanElement>(null);
    const focused = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || focused.current) return;
        if (el.textContent !== value) el.textContent = value;
    }, [value]);

    return (
        <span
            ref={ref}
            className={className}
            contentEditable
            suppressContentEditableWarning
            onFocus={() => { focused.current = true; }}
            onBlur={() => {
                focused.current = false;
                onChange((ref.current?.textContent ?? "").replace(/\u00a0/g, " ").trim());
            }}
            onMouseDown={event => event.stopPropagation()}
            onKeyDown={event => {
                event.stopPropagation();
                if (event.key === "Enter") {
                    event.preventDefault();
                    (event.currentTarget as HTMLElement).blur();
                }
            }}
        >
            {value}
        </span>
    );
}

/** The in-editor view. Heading and footer are editable; slots are a live
 *  preview of the email markup. Style switcher + drag handle on the chrome. */
function SuggestedTimesNodeView({ node, updateAttributes, deleteNode, selected }: NodeViewProps) {
    const attrs = attrsFromNode(node.attrs as Record<string, unknown>);
    // Slots only — heading and footer are editable regions around this preview.
    const slotsHtml = buildSuggestedTimesHtml({
        ...attrs,
        omitHeading: true,
        omitFooter: true,
    });

    return (
        <NodeViewWrapper
            className="suggested-times-node"
            data-selected={selected ? "true" : undefined}
        >
            <div className="suggested-times-node__chrome" contentEditable={false}>
                <span
                    className="suggested-times-node__handle"
                    data-drag-handle
                    draggable="true"
                    contentEditable={false}
                    title="Drag to move"
                    aria-label="Drag suggested times block"
                >
                    ⠿
                </span>
                <span className="suggested-times-node__label">🗓 Suggested times</span>
                <span className="suggested-times-node__spacer" />
                <span className="suggested-times-node__styles" role="radiogroup" aria-label="Suggested times style">
                    {STYLE_OPTIONS.map(opt => {
                        const on = attrs.style === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                tabIndex={-1}
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => updateAttributes({ style: opt.value })}
                                className={
                                    "suggested-times-node__style" + (on ? " is-active" : "")
                                }
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </span>
                <button
                    type="button"
                    className="suggested-times-node__delete"
                    tabIndex={-1}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => deleteNode()}
                    title="Remove suggested times"
                    aria-label="Remove suggested times"
                >
                    ✕
                </button>
            </div>
            <div
                className="suggested-times-node__preview"
                data-style={attrs.style}
            >
                {/* Editable heading: typing here edits the node's inline content,
                    which renderToReactEmail reads back on send. */}
                <NodeViewContent<"p">
                    as="p"
                    className="suggested-times-node__heading"
                />
                <div
                    className="suggested-times-node__body"
                    contentEditable={false}
                    dangerouslySetInnerHTML={{ __html: slotsHtml }}
                />
                <p className="suggested-times-node__footer" contentEditable={false}>
                    <EditableAttr
                        className="suggested-times-node__footer-text"
                        value={attrs.footer ?? DEFAULT_SUGGESTED_TIMES_FOOTER}
                        onChange={footer => updateAttributes({ footer })}
                    />
                    {" "}
                    <EditableAttr
                        className="suggested-times-node__more-label"
                        value={attrs.moreLabel ?? DEFAULT_SUGGESTED_TIMES_MORE}
                        onChange={moreLabel => updateAttributes({ moreLabel })}
                    />
                    {(attrs.moreLabel ?? DEFAULT_SUGGESTED_TIMES_MORE) ? "." : ""}
                </p>
            </div>
        </NodeViewWrapper>
    );
}

/**
 * The custom EmailNode. `content: "inline*"` makes the heading editable inline
 * content; the block is still selectable + draggable so it behaves as one
 * object. `group: "block"` so it sits at the top level of the doc where the
 * serializer iterates it. `defining` keeps the node intact through edits (so
 * editing the heading never accidentally splits or unwraps the block).
 */
export const SuggestedTimesNode = EmailNode.create({
    name: "suggestedTimes",
    group: "block",
    content: "inline*",
    selectable: true,
    draggable: true,
    defining: true,
    isolating: true,

    addAttributes() {
        return {
            token: { default: "" },
            // slots round-trips as a JSON string in the DOM data attr but lives
            // as a string[] inside the editor's JSON.
            slots: {
                default: [] as string[],
                parseHTML: (element: HTMLElement) =>
                    safeParseSlots(element.getAttribute("data-slots") ?? "[]"),
                renderHTML: (attributes: { slots?: string[] }) => ({
                    "data-slots": JSON.stringify(attributes.slots ?? []),
                }),
            },
            slug: { default: undefined },
            timeZone: { default: "America/New_York" },
            bookingUrlBase: { default: "" },
            footer: {
                default: DEFAULT_SUGGESTED_TIMES_FOOTER,
                parseHTML: (element: HTMLElement) =>
                    element.getAttribute("data-footer") ?? DEFAULT_SUGGESTED_TIMES_FOOTER,
                renderHTML: (attributes: { footer?: string }) => ({
                    "data-footer": attributes.footer ?? DEFAULT_SUGGESTED_TIMES_FOOTER,
                }),
            },
            moreLabel: {
                default: DEFAULT_SUGGESTED_TIMES_MORE,
                parseHTML: (element: HTMLElement) =>
                    element.getAttribute("data-more-label") ?? DEFAULT_SUGGESTED_TIMES_MORE,
                renderHTML: (attributes: { moreLabel?: string }) => ({
                    "data-more-label": attributes.moreLabel ?? DEFAULT_SUGGESTED_TIMES_MORE,
                }),
            },
            style: { default: "text" as SuggestedTimesStyle },
        };
    },

    parseHTML() {
        return [{ tag: "div[data-suggested-times]" }];
    },

    renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
        // Editor-side / clipboard fallback only. Carries the attrs; the `0` hole
        // is where the heading content (inline text) is serialized. The real
        // email markup comes from renderToReactEmail.
        return ["div", mergeAttributes(HTMLAttributes, { "data-suggested-times": "" }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(SuggestedTimesNodeView);
    },

    renderToReactEmail: SuggestedTimesEmail,
});
